"use client";

import Link from "next/link";
import { ChevronRight } from "@/components/icons";
import { cn } from "@/lib/utils";
import { TONE_TINT } from "@/components/ui/tone";
import type { ActivityItem } from "./use-activity-feed";

interface ActivityFeedListProps {
  items: ActivityItem[];
  limit?: number;
  onNavigate?: () => void;
  className?: string;
}

export function ActivityFeedList({
  items,
  limit,
  onNavigate,
  className,
}: ActivityFeedListProps) {
  const visible = limit ? items.slice(0, limit) : items;
  return (
    <ul className={cn("divide-y divide-border/60", className)}>
      {visible.map((item) => (
        <ActivityRow key={item.id} item={item} onNavigate={onNavigate} />
      ))}
    </ul>
  );
}

function ActivityRow({
  item,
  onNavigate,
}: {
  item: ActivityItem;
  onNavigate?: () => void;
}) {
  const rowClassName = "flex items-center gap-3 px-4 py-3";
  if (!item.href) {
    return (
      <li className={rowClassName}>
        <ActivityRowBody item={item} />
      </li>
    );
  }
  return (
    <li>
      <Link
        href={item.href}
        prefetch={false}
        onClick={onNavigate}
        className={cn(
          "ph-no-capture group transition-colors hover:bg-muted/40",
          rowClassName,
        )}
      >
        <ActivityRowBody item={item} />
        <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
      </Link>
    </li>
  );
}

function ActivityRowBody({ item }: { item: ActivityItem }) {
  const Icon = item.icon;
  return (
    <>
      <div
        className={cn(
          "flex size-9 shrink-0 items-center justify-center rounded-md",
          item.priority <= 1 ? TONE_TINT.warning : TONE_TINT.neutral,
        )}
      >
        <Icon className="size-4" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-foreground group-hover:text-action">
          {item.title}
        </p>
        <p className="truncate text-xs text-muted-foreground">
          {item.subtitle}
        </p>
      </div>
    </>
  );
}
